import { useCallback, useRef, useState } from 'react';
import type { AgentEvent, CardPayload } from '@lumlix/core/events';
import { AgentClient, type ConnectionState } from './agentClient.ts';
import { getIdToken } from './firebase.ts';

export interface ChatItem {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  cards: CardPayload[];
  streaming: boolean;
}

export interface ToolActivity {
  callId: string;
  tool: string;
  ok?: boolean;
  args: unknown;
}

export interface TimelineEntry {
  at: number;
  label: string;
  node?: string;
}

const client = new AgentClient({
  baseUrl: import.meta.env.VITE_AGENT_BASE_URL ?? '',
  getIdToken,
});

/**
 * Owns one conversation thread. Segment handoffs never surface as separate messages —
 * the assistant turn keeps accumulating across however many segments the run takes.
 */
export function useRun(threadId: string) {
  const [items, setItems] = useState<ChatItem[]>([]);
  const [tools, setTools] = useState<ToolActivity[]>([]);
  const [timeline, setTimeline] = useState<TimelineEntry[]>([]);
  const [conn, setConn] = useState<ConnectionState>({ phase: 'idle' });
  const [pendingApproval, setPendingApproval] = useState<string | null>(null);
  const runIdRef = useRef<string | null>(null);
  const handleRef = useRef<{ abort: () => void } | null>(null);

  const consume = useCallback(async (events: AsyncIterable<AgentEvent>, assistantId: string) => {
    for await (const ev of events) {
      switch (ev.type) {
        case 'run_started':
          runIdRef.current = ev.runId;
          break;

        case 'token':
          setItems((prev) =>
            prev.map((m) => (m.id === assistantId ? { ...m, text: m.text + ev.delta } : m)),
          );
          break;

        case 'node_started':
          setTimeline((t) => [...t, { at: Date.now(), label: ev.label, node: ev.node }]);
          break;

        case 'tool_started':
          setTools((t) => [...t, { callId: ev.callId, tool: ev.tool, args: ev.args }]);
          break;

        case 'tool_result':
          setTools((t) => t.map((x) => (x.callId === ev.callId ? { ...x, ok: ev.ok } : x)));
          break;

        case 'card':
          setItems((prev) =>
            prev.map((m) => (m.id === assistantId ? { ...m, cards: [...m.cards, ev.card] } : m)),
          );
          break;

        case 'approval_required':
          setPendingApproval(ev.approvalId);
          setItems((prev) =>
            prev.map((m) =>
              m.id === assistantId
                ? {
                    ...m,
                    streaming: false,
                    cards: [
                      ...m.cards,
                      {
                        kind: 'approval',
                        approvalId: ev.approvalId,
                        title: ev.summary,
                        rows: approvalRows(ev.kind, ev.diff),
                      },
                    ],
                  }
                : m,
            ),
          );
          break;

        case 'run_finished':
          setItems((prev) =>
            prev.map((m) => (m.id === assistantId ? { ...m, streaming: false } : m)),
          );
          break;

        case 'error':
          setItems((prev) =>
            prev.map((m) =>
              m.id === assistantId
                ? { ...m, streaming: false, text: m.text + `\n\n⚠ ${ev.message}` }
                : m,
            ),
          );
          break;
      }
    }
  }, []);

  const send = useCallback(
    async (message: string) => {
      const userId = crypto.randomUUID();
      const assistantId = crypto.randomUUID();
      setItems((prev) => [
        ...prev,
        { id: userId, role: 'user', text: message, cards: [], streaming: false },
        { id: assistantId, role: 'assistant', text: '', cards: [], streaming: true },
      ]);
      setTools([]);
      const handle = client.start({ threadId, message }, setConn);
      handleRef.current = handle;
      await consume(handle.events, assistantId);
    },
    [threadId, consume],
  );

  const decide = useCallback(
    async (approvalId: string, decision: 'approve' | 'reject') => {
      const runId = runIdRef.current;
      if (!runId) return;
      setPendingApproval(null);
      const assistantId = crypto.randomUUID();
      setItems((prev) => [
        ...prev,
        { id: assistantId, role: 'assistant', text: '', cards: [], streaming: true },
      ]);
      const handle = client.approve(runId, approvalId, decision, undefined, setConn);
      handleRef.current = handle;
      await consume(handle.events, assistantId);
    },
    [consume],
  );

  const abort = useCallback(() => {
    handleRef.current?.abort();
    setConn({ phase: 'idle' });
  }, []);

  return { items, tools, timeline, conn, pendingApproval, send, decide, abort };
}

function approvalRows(kind: string, diff: unknown): Array<{ label: string; value: string; risk?: 'spend' | 'publish' | 'change' }> {
  const risk = kind === 'update_budget' || kind === 'launch_campaign' ? 'spend'
    : kind === 'publish_now' ? 'publish'
    : 'change';
  const d = (diff ?? {}) as Record<string, unknown>;
  return Object.entries(d).slice(0, 8).map(([label, value]) => ({
    label,
    value: typeof value === 'object' ? JSON.stringify(value) : String(value),
    risk: label.toLowerCase().includes('budget') || label.toLowerCase().includes('spend') ? 'spend' : risk,
  }));
}
